import { useEffect, useMemo, useState } from "react";
import {
  Wallet,
  ChartLineUp,
  Hourglass,
  CalendarCheck,
  Receipt,
  Info,
  CaretDown,
  ArrowCounterClockwise,
  X,
} from "@phosphor-icons/react";
import HoverTip from "../ui/HoverTip";
import { formatHebrewDate } from "../../utils/dateUtils";

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "—";
  return `₪${amount.toLocaleString("he-IL")}`;
};

const sumItems = (items) =>
  items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);

/**
 * Five KPI tiles for the current month.
 *
 * Each tile is clickable: it expands a breakdown panel listing the rows the
 * number is built from (patient · date · amount). Clicking a row excludes it
 * from the tile's displayed total ("what would this be without X?"). The
 * exclusion is view-only — nothing is written — and resets when the row
 * disappears from the data (e.g. it was paid / refetched away).
 *
 * `*Items` arrays are the breakdown source; the `*Amount` props stay the
 * server totals so the tile is never off from the aggregation.
 */
function FinanceStatTiles({
  actualIncome,
  expectedIncome,
  pendingAmount,
  futureAmount,
  invoicesAmount,
  actualItems = [],
  expectedItems = [],
  pendingItems = [],
  futureItems = [],
  invoiceItems = [],
  isLoading,
}) {
  const [openKey, setOpenKey] = useState(null);
  // { [tileKey]: Set<itemId> } — excluded (toggled off) rows per tile.
  const [excluded, setExcluded] = useState({});

  const tiles = useMemo(
    () => [
      {
        key: "actual",
        icon: Wallet,
        label: "הכנסה בפועל החודש",
        value: actualIncome,
        items: actualItems,
        info: "סך התשלומים שהתקבלו בפועל החודש — כולל תשלומים שהופקה עבורם חשבונית. הכנסה נזקפת לחודש שבו התקבל התשלום.",
      },
      {
        key: "all",
        icon: ChartLineUp,
        label: "צפי הכנסה החודש",
        value: expectedIncome,
        items: expectedItems,
        info: "תשלומים צפויים שמועד הפגישה שלהם החודש וטרם הגיע.",
      },
      {
        key: "pending",
        icon: Hourglass,
        label: "ממתין לתשלום",
        value: pendingAmount,
        items: pendingItems,
        info: "כל התשלומים שטרם נגבו — מכל התאריכים, לא רק החודש.",
      },
      {
        key: "expected",
        icon: CalendarCheck,
        label: "תשלומים עתידיים",
        value: futureAmount,
        items: futureItems,
        info: "תשלומים צפויים לפגישות עתידיות — מכל התאריכים.",
      },
      {
        key: "invoiced",
        icon: Receipt,
        label: "חשבוניות החודש",
        value: invoicesAmount,
        items: invoiceItems,
        info: "סך החשבוניות שהופקו החודש.",
      },
    ],
    [
      actualIncome,
      expectedIncome,
      pendingAmount,
      futureAmount,
      invoicesAmount,
      actualItems,
      expectedItems,
      pendingItems,
      futureItems,
      invoiceItems,
    ]
  );

  // Drop exclusions for rows that no longer exist in the data.
  useEffect(() => {
    setExcluded((prev) => {
      let changed = false;
      const next = {};
      for (const tile of tiles) {
        const set = prev[tile.key];
        if (!set || set.size === 0) continue;
        const live = new Set(tile.items.map((it) => it.id));
        const kept = new Set([...set].filter((id) => live.has(id)));
        if (kept.size !== set.size) changed = true;
        if (kept.size > 0) next[tile.key] = kept;
      }
      return changed ? next : prev;
    });
  }, [tiles]);

  const excludedFor = (key) => excluded[key] || new Set();

  const displayValue = (tile) => {
    const set = excludedFor(tile.key);
    if (set.size === 0) return tile.value;
    const removed = sumItems(tile.items.filter((it) => set.has(it.id)));
    return (tile.value || 0) - removed;
  };

  const toggleOpen = (key) => setOpenKey((k) => (k === key ? null : key));

  const toggleItem = (tileKey, itemId) => {
    setExcluded((prev) => {
      const set = new Set(prev[tileKey] || []);
      if (set.has(itemId)) set.delete(itemId);
      else set.add(itemId);
      const next = { ...prev };
      if (set.size > 0) next[tileKey] = set;
      else delete next[tileKey];
      return next;
    });
  };

  const resetTile = (tileKey) =>
    setExcluded((prev) => {
      if (!prev[tileKey]) return prev;
      const next = { ...prev };
      delete next[tileKey];
      return next;
    });

  const openTile = tiles.find((t) => t.key === openKey) || null;
  const openExcluded = openTile ? excludedFor(openTile.key) : new Set();

  const onTileKey = (e, key) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleOpen(key);
    }
  };

  const onRowKey = (e, tileKey, itemId) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleItem(tileKey, itemId);
    }
  };

  return (
    <div className="fin-stat-wrap">
      <div className="fin-stat-tiles">
        {tiles.map((tile) => {
          const Icon = tile.icon;
          const isOpen = openKey === tile.key;
          const excludedCount = excludedFor(tile.key).size;
          return (
            <div
              key={tile.key}
              className={`fin-stat-tile ${isOpen ? "is-open" : ""} ${
                excludedCount > 0 ? "has-excluded" : ""
              }`}
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              onClick={() => toggleOpen(tile.key)}
              onKeyDown={(e) => onTileKey(e, tile.key)}
            >
              <div className="fin-stat-icon">
                <Icon size={16} weight="fill" />
              </div>
              <div className="fin-stat-text">
                <span className="fin-stat-value">
                  {isLoading ? "..." : formatCurrency(displayValue(tile))}
                  {excludedCount > 0 && !isLoading && (
                    <span className="fin-stat-excluded-badge">
                      ללא {excludedCount}
                    </span>
                  )}
                </span>
                <span className="fin-stat-label">
                  {tile.label}
                  {tile.info && (
                    <HoverTip text={tile.info}>
                      <span
                        className="fin-stat-info"
                        tabIndex={0}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Info size={12} weight="bold" />
                      </span>
                    </HoverTip>
                  )}
                </span>
              </div>
              <CaretDown
                size={14}
                weight="bold"
                className="fin-stat-caret"
                aria-hidden="true"
              />
            </div>
          );
        })}
      </div>

      {openTile && (
        <div className="fin-stat-breakdown" role="region" aria-live="polite">
          <div className="fin-stat-breakdown-head">
            <div className="fin-stat-breakdown-title">
              <span>ממה מורכב "{openTile.label}"</span>
              <span className="fin-stat-breakdown-count">
                {openTile.items.length} פריטים
              </span>
            </div>
            <div className="fin-stat-breakdown-actions">
              {openExcluded.size > 0 && (
                <button
                  type="button"
                  className="fin-stat-breakdown-reset"
                  onClick={() => resetTile(openTile.key)}
                >
                  <ArrowCounterClockwise size={13} weight="bold" />
                  החזר הכל
                </button>
              )}
              <button
                type="button"
                className="fin-stat-breakdown-close"
                aria-label="סגור פירוט"
                onClick={() => setOpenKey(null)}
              >
                <X size={14} weight="bold" />
              </button>
            </div>
          </div>

          <p className="fin-stat-breakdown-hint">
            לחיצה על שורה מורידה אותה מהחישוב (לצפייה בלבד — לא משנה נתונים).
          </p>

          {openTile.items.length === 0 ? (
            <div className="fin-stat-breakdown-empty">אין פריטים החודש</div>
          ) : (
            <ul className="fin-stat-breakdown-list">
              {openTile.items.map((it) => {
                const off = openExcluded.has(it.id);
                return (
                  <li
                    key={it.id}
                    className={`fin-stat-breakdown-row ${off ? "is-off" : ""}`}
                    role="button"
                    tabIndex={0}
                    aria-pressed={off}
                    onClick={() => toggleItem(openTile.key, it.id)}
                    onKeyDown={(e) => onRowKey(e, openTile.key, it.id)}
                  >
                    <span className="fin-stat-breakdown-dot" aria-hidden="true" />
                    <span className="fin-stat-breakdown-name">
                      {it.patientName || it.description || "ללא שם"}
                    </span>
                    <span className="fin-stat-breakdown-date">
                      {it.date ? formatHebrewDate(it.date, "dd/MM") : ""}
                    </span>
                    <span className="fin-stat-breakdown-amount">
                      {formatCurrency(it.amount)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          {openExcluded.size > 0 && (
            <div className="fin-stat-breakdown-foot">
              <span>סה"כ ללא {openExcluded.size} פריטים</span>
              <strong>{formatCurrency(displayValue(openTile))}</strong>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default FinanceStatTiles;
